/**
 * Better Auth error → i18n key, shared by flashka-web and flashka-app.
 *
 * Raw API messages never reach the UI: every code the auth flows can produce
 * maps to translated copy, unknown codes fall back to a generic message and
 * are logged for operators. Both apps ship the SAME `auth.errors.*` key set
 * in their locale files, so the literal union below type-checks against
 * either app's generated i18n key union without a cast.
 */
export type AuthErrorKey =
  | 'auth.errors.invalidCredentials'
  | 'auth.errors.invalidOtp'
  | 'auth.errors.tooManyAttempts'
  | 'auth.errors.emailExists'
  | 'auth.errors.weakPassword'
  | 'auth.errors.emailNotVerified'
  | 'auth.errors.rateLimited'
  | 'auth.errors.reauthRequired'
  | 'auth.errors.generic';

export type AuthErrorLike =
  | {
      code?: string;
      status?: number;
      message?: string;
      statusText?: string;
      /** Seconds to wait, on a 429 the client passed through `exposeRetryAfter`. */
      retryAfter?: number;
    }
  | null
  | undefined;

const CODE_TO_KEY: Record<string, AuthErrorKey> = {
  INVALID_EMAIL_OR_PASSWORD: 'auth.errors.invalidCredentials',
  INVALID_PASSWORD: 'auth.errors.invalidCredentials',
  INVALID_OTP: 'auth.errors.invalidOtp',
  OTP_EXPIRED: 'auth.errors.invalidOtp',
  TOO_MANY_ATTEMPTS: 'auth.errors.tooManyAttempts',
  // The installed worker (better-auth 1.6.23) throws the long code from the
  // sign-up route's duplicate-email branch, not the shorter USER_ALREADY_EXISTS
  // — both are mapped in case a config tweak changes which one surfaces.
  USER_ALREADY_EXISTS: 'auth.errors.emailExists',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'auth.errors.emailExists',
  PASSWORD_TOO_SHORT: 'auth.errors.weakPassword',
  PASSWORD_TOO_LONG: 'auth.errors.weakPassword',
  EMAIL_NOT_VERIFIED: 'auth.errors.emailNotVerified',
  // `freshSessionMiddleware` (list-sessions, unlink-account): the session is
  // live but older than the worker's `freshAge`. "Try again" would be a lie —
  // only a new sign-in clears it.
  SESSION_NOT_FRESH: 'auth.errors.reauthRequired',
};

export function mapAuthError(error: AuthErrorLike): AuthErrorKey {
  if (error?.status === 429) return 'auth.errors.rateLimited';
  const key = error?.code ? CODE_TO_KEY[error.code] : undefined;
  if (!key && error?.code) {
    // CODE AND STATUS ONLY. This line runs in the SHIPPED web bundle, and the
    // server's own `message` can echo what the user typed — so the detail that
    // `describeAuthError` adds is for dev surfaces and E2E output, which call
    // it themselves, not for a stranger's browser console.
    console.error(
      '[auth] unmapped error:',
      `${error.code}${typeof error.status === 'number' ? ` · HTTP ${error.status}` : ''}`,
    );
  }
  return key ?? 'auth.errors.generic';
}

/**
 * The same error as a short TECHNICAL line — `code`, HTTP status, and the
 * server's own message — for logs, dev surfaces and E2E output.
 *
 * NOT user-facing copy, and not a relaxation of the rule above it: the UI
 * still shows `t(mapAuthError(error))`. This exists because the key ALONE is
 * useless to whoever has to fix the failure — `auth.errors.generic` on a
 * screen and in a log says only "something went wrong", and the one fact that
 * would have identified it (the code, or a 401 vs a 500) was being discarded
 * at the mapping. Returns null when the error carries nothing worth printing,
 * so a caller can omit the detail rather than print an empty parenthesis.
 */
export function describeAuthError(error: AuthErrorLike): string | null {
  if (!error) return null;
  const parts: string[] = [];
  if (error.code) parts.push(error.code);
  if (typeof error.status === 'number')
    parts.push(`HTTP ${error.status}${error.statusText ? ` ${error.statusText}` : ''}`);
  // The server's message last: it is the longest and the least structured.
  if (error.message && error.message !== error.code) parts.push(error.message);
  return parts.length ? parts.join(' · ') : null;
}
